import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  CheckSquare,
  Search,
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  User,
  ArrowRight,
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  in_progress: { label: 'In Progress', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500' },
};

const priorityConfig = {
  high: { label: 'High', color: 'text-red-600' },
  medium: { label: 'Medium', color: 'text-amber-600' },
  low: { label: 'Low', color: 'text-slate-600' },
};

const StaffTasksQueuePage = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const statusFilter = searchParams.get('status') || 'all';
  const assignedFilter = searchParams.get('assigned') || 'all';
  const priorityFilter = searchParams.get('priority') || 'all';

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (assignedFilter === 'me') params.append('assigned_to', 'me');
      else if (assignedFilter === 'unassigned') params.append('assigned_to', 'unassigned');
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      params.append('limit', '100');

      const response = await axios.get(`${API}/portal/staff/queue/tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTasks(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      setError(err.response?.data?.detail || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchTasks();
  }, [token, statusFilter, assignedFilter, priorityFilter]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  const filteredTasks = tasks.filter(t => {
    if (!searchTerm) return true;
    const title = t.title?.toLowerCase() || '';
    const description = t.description?.toLowerCase() || '';
    return title.includes(searchTerm.toLowerCase()) || 
           description.includes(searchTerm.toLowerCase());
  });

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-tasks-queue">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Task Queue</h1>
          <p className="text-slate-500 mt-1">Manage and track all tasks</p>
        </div>
        <Button onClick={fetchTasks} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => updateFilter('status', v)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={assignedFilter} onValueChange={(v) => updateFilter('assigned', v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Assignment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="me">Assigned to Me</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={(v) => updateFilter('priority', v)}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-slate-500">
        <span>Showing {filteredTasks.length} of {total} tasks</span>
      </div>

      {/* Tasks List */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-100 rounded" />
            ))}
          </div>
        </div>
      ) : filteredTasks.length > 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {filteredTasks.map((task) => {
            const status = statusConfig[task.status] || statusConfig.pending;
            const priority = priorityConfig[task.priority] || priorityConfig.low;
            const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
            return (
              <div
                key={task.task_id}
                className="p-4 hover:bg-slate-50 transition-colors"
                data-testid={`task-row-${task.task_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    task.status === 'completed' ? 'bg-emerald-100' : 'bg-slate-100'
                  }`}>
                    {task.status === 'completed' ? (
                      <CheckCircle size={20} className="text-emerald-600" />
                    ) : (
                      <CheckSquare size={20} className="text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-stone-900">{task.title || 'Untitled Task'}</h3>
                        {task.description && (
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{task.description}</p>
                        )}
                      </div>
                      <span className={`flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                      <span className={`font-medium ${priority.color}`}>{priority.label} Priority</span>
                      {task.assigned_to_user_id && (
                        <span className="flex items-center gap-1">
                          <User size={12} />
                          Assigned
                        </span>
                      )}
                      {task.due_date && (
                        <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}>
                          <Clock size={12} />
                          Due {new Date(task.due_date).toLocaleDateString()}
                          {isOverdue && ' (Overdue)'}
                        </span>
                      )}
                      <Link 
                        to={`/staff/cases/${task.case_id}`}
                        className="text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                      >
                        View Case <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <CheckSquare size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No tasks found</h3>
          <p className="text-slate-500">
            {searchTerm || statusFilter !== 'all' || assignedFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'All tasks have been completed'}
          </p>
        </div>
      )}
    </div>
  );
};

export default StaffTasksQueuePage;
